import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { Plus, Pencil, Trash2, X, RotateCcw } from "lucide-react";
import formatApiError from "../../lib/apiError";
import useActiveInactive from "../../lib/useActiveInactive";
import ActiveInactiveTabs from "../../components/ActiveInactiveTabs";

const API = process.env.REACT_APP_BACKEND_URL;
const ax = () => ({ withCredentials: true });
const BASE = `${API}/api/colleges`;

const COUNTRY_CODES = [
  { code: "+91",  label: "+91 India" },
  { code: "+1",   label: "+1 US/Canada" },
  { code: "+44",  label: "+44 UK" },
  { code: "+61",  label: "+61 Australia" },
  { code: "+971", label: "+971 UAE" },
  { code: "+966", label: "+966 Saudi Arabia" },
  { code: "+974", label: "+974 Qatar" },
  { code: "+968", label: "+968 Oman" },
  { code: "+973", label: "+973 Bahrain" },
  { code: "+65",  label: "+65 Singapore" },
  { code: "+60",  label: "+60 Malaysia" },
  { code: "+92",  label: "+92 Pakistan" },
  { code: "+880", label: "+880 Bangladesh" },
];

const EMPTY_FORM = {
  name: "",
  office_email: "", office_phone_code: "+91", office_phone: "", address: "",
  country_id: "", state_id: "", city_id: "",
  university_id: "",
  admin_name: "", admin_email: "", admin_phone_code: "+91", admin_phone: "",
  it_admin_name: "", it_admin_email: "", it_admin_phone_code: "+91", it_admin_phone: "",
};

function PhoneField({ codeKey, numberKey, form, onChange, testPrefix }) {
  return (
    <div className="phone-field">
      <select className="field-input phone-code-select" value={form[codeKey]} onChange={(e) => onChange(codeKey)(e)} data-testid={`${testPrefix}-code`}>
        {COUNTRY_CODES.map((c) => (<option key={c.code} value={c.code}>{c.code}</option>))}
      </select>
      <input className="field-input phone-number-input" placeholder="Phone number" value={form[numberKey]}
        onChange={(e) => onChange(numberKey)(e)} maxLength={20} data-testid={`${testPrefix}-number`} />
    </div>
  );
}

export default function CollegesPage() {
  const { view, setView, list: colleges, inactiveCount, loading, error, reload } = useActiveInactive(BASE);

  const [countries, setCountries] = useState([]);
  const [states, setStates]       = useState([]);
  const [cities, setCities]       = useState([]);
  const [universities, setUniversities] = useState([]);

  const [modal, setModal]         = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [formErr, setFormErr]     = useState("");
  const [saving, setSaving]       = useState(false);

  const [confirmId, setConfirmId] = useState(null);
  const [deleting, setDeleting]   = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  const statesForCountry = useMemo(
    () => states.filter((s) => s.country_id === form.country_id),
    [states, form.country_id]
  );
  const citiesForState = useMemo(
    () => cities.filter((c) => c.state_id === form.state_id),
    [cities, form.state_id]
  );

  const onChange = (key) => (e) => {
    const value = e.target.value;
    setForm((f) => {
      const next = { ...f, [key]: value };
      if (key === "country_id") { next.state_id = ""; next.city_id = ""; }
      if (key === "state_id")   { next.city_id = ""; }
      return next;
    });
  };

  const fetchAux = useCallback(async () => {
    try {
      const [ctyRes, stRes, ciRes, uniRes] = await Promise.all([
        axios.get(`${API}/api/countries`,    ax()),
        axios.get(`${API}/api/states`,       ax()),
        axios.get(`${API}/api/cities`,       ax()),
        axios.get(`${API}/api/universities`, ax()),
      ]);
      setCountries(ctyRes.data);
      setStates(stRes.data);
      setCities(ciRes.data);
      setUniversities(uniRes.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchAux(); }, [fetchAux]);

  const openAdd  = () => { setForm(EMPTY_FORM); setFormErr(""); setModal({ mode: "add", college: null }); };
  const openEdit = (c) => {
    setForm({
      ...EMPTY_FORM, ...c,
      country_id: c.country_id || "",
      state_id: c.state_id || "",
      city_id: c.city_id || "",
      university_id: c.university_id || "",
    });
    setFormErr(""); setModal({ mode: "edit", college: c });
  };
  const closeModal = () => { setModal(null); setFormErr(""); };

  const handleSave = async () => {
    if (!form.name.trim()) { setFormErr("College name is required."); return; }
    setSaving(true); setFormErr("");
    const payload = {
      ...form,
      country_id:    form.country_id    || null,
      state_id:      form.state_id      || null,
      city_id:       form.city_id       || null,
      university_id: form.university_id || null,
    };
    try {
      if (modal.mode === "add") await axios.post(BASE, payload, ax());
      else                       await axios.put(`${BASE}/${modal.college.id}`, payload, ax());
      closeModal(); reload();
    } catch (e) { setFormErr(formatApiError(e, "Something went wrong.")); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try { await axios.delete(`${BASE}/${confirmId}`, ax()); setConfirmId(null); reload(); }
    catch { setConfirmId(null); }
    finally { setDeleting(false); }
  };

  const handleRestore = async (id) => {
    setRestoringId(id);
    try { await axios.post(`${BASE}/${id}/restore`, {}, ax()); reload(); }
    catch (e) { alert(formatApiError(e, "Could not restore.")); }
    finally { setRestoringId(null); }
  };

  const fmtPhone = (code, num) => (num ? `${code} ${num}` : "");

  return (
    <div className="page-wrap">
      <div className="page-header-row">
        <div>
          <h1 className="page-title">Colleges</h1>
          <p className="page-sub">Manage registered medical colleges and their contacts.</p>
        </div>
        {view === "active" && (
          <button className="btn-primary" onClick={openAdd} data-testid="add-college-btn">
            <Plus size={16} /> Add College
          </button>
        )}
      </div>

      <ActiveInactiveTabs view={view} onChange={setView} inactiveCount={inactiveCount} testIdPrefix="colleges-view" />

      {loading ? <div className="table-loading">Loading…</div>
       : error   ? <div className="table-error">{error}</div>
       : (
        <div className="data-card">
          {colleges.length === 0 ? (
            <div className="empty-state">
              {view === "active"
                ? <p>No colleges yet. Click <strong>Add College</strong> to create one.</p>
                : <p>No inactive colleges.</p>}
            </div>
          ) : (
            <div className="table-scroll-wrap">
            <table className="data-table" data-testid="colleges-table">
              <thead>
                <tr>
                  <th style={{ width: 48 }}>#</th>
                  <th>Name</th>
                  <th>Location</th>
                  <th>Office Email</th>
                  <th>Office Phone</th>
                  <th>College Admin</th>
                  <th>IT Admin</th>
                  <th style={{ width: 110 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {colleges.map((c, i) => (
                  <tr key={c.id} data-testid={`college-row-${c.id}`}>
                    <td className="td-num">{i + 1}</td>
                    <td className="td-name">{c.name}</td>
                    <td className="td-desc">
                      {c.city_name || c.state_name || c.country_name ? (
                        <div>
                          {c.city_name && <div className="td-person-name">{c.city_name}</div>}
                          <div className="td-person-sub">
                            {[c.state_name, c.country_name].filter(Boolean).join(", ")}
                          </div>
                        </div>
                      ) : <span className="td-empty">—</span>}
                    </td>
                    <td className="td-desc">{c.office_email || <span className="td-empty">—</span>}</td>
                    <td className="td-desc">{fmtPhone(c.office_phone_code, c.office_phone) || <span className="td-empty">—</span>}</td>
                    <td>
                      {c.admin_name ? (
                        <div>
                          <div className="td-person-name">{c.admin_name}</div>
                          {c.admin_email && <div className="td-person-sub">{c.admin_email}</div>}
                          {c.admin_phone && <div className="td-person-sub">{fmtPhone(c.admin_phone_code, c.admin_phone)}</div>}
                        </div>
                      ) : <span className="td-empty">—</span>}
                    </td>
                    <td>
                      {c.it_admin_name ? (
                        <div>
                          <div className="td-person-name">{c.it_admin_name}</div>
                          {c.it_admin_email && <div className="td-person-sub">{c.it_admin_email}</div>}
                          {c.it_admin_phone && <div className="td-person-sub">{fmtPhone(c.it_admin_phone_code, c.it_admin_phone)}</div>}
                        </div>
                      ) : <span className="td-empty">—</span>}
                    </td>
                    <td>
                      <div className="action-btns">
                        {view === "active" ? (
                          <>
                            <button className="action-edit"   onClick={() => openEdit(c)}        title="Edit"   data-testid={`edit-college-${c.id}`}><Pencil size={15} /></button>
                            <button className="action-delete" onClick={() => setConfirmId(c.id)} title="Delete" data-testid={`delete-college-${c.id}`}><Trash2 size={15} /></button>
                          </>
                        ) : (
                          <button className="action-restore" onClick={() => handleRestore(c.id)} disabled={restoringId === c.id} data-testid={`restore-college-${c.id}`} title="Restore"><RotateCcw size={15} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-box modal-box-lg" onClick={(e) => e.stopPropagation()} data-testid="college-modal">
            <div className="modal-head">
              <h2 className="modal-title">{modal.mode === "add" ? "Add College" : "Edit College"}</h2>
              <button className="modal-close" onClick={closeModal}><X size={18} /></button>
            </div>
            {formErr && <div className="form-error" data-testid="form-error">{formErr}</div>}
            <div className="modal-body modal-body-scroll">

              <div className="form-section">
                <p className="form-section-title">College Information</p>
                <div className="field">
                  <label className="field-label">Name <span className="required">*</span></label>
                  <input className="field-input" placeholder="e.g. AIIMS New Delhi" value={form.name} onChange={onChange("name")} maxLength={200} autoFocus data-testid="college-name-input" />
                </div>
                <div className="field-row">
                  <div className="field">
                    <label className="field-label">Office Email</label>
                    <input className="field-input" type="email" placeholder="office@college.edu" value={form.office_email} onChange={onChange("office_email")} data-testid="college-email-input" />
                  </div>
                  <div className="field">
                    <label className="field-label">Office Phone</label>
                    <PhoneField codeKey="office_phone_code" numberKey="office_phone" form={form} onChange={onChange} testPrefix="office-phone" />
                  </div>
                </div>
                <div className="field">
                  <label className="field-label">Address</label>
                  <textarea className="field-input field-textarea" placeholder="Full address…" value={form.address} onChange={onChange("address")} rows={2} maxLength={500} data-testid="college-address-input" />
                </div>
                <div className="field-row">
                  <div className="field">
                    <label className="field-label">Country</label>
                    <select className="field-input" value={form.country_id} onChange={onChange("country_id")} data-testid="college-country-select">
                      <option value="">Select…</option>
                      {countries.map((c) => (<option key={c.id} value={c.id}>{c.country_code} — {c.name}</option>))}
                    </select>
                  </div>
                  <div className="field">
                    <label className="field-label">State</label>
                    <select className="field-input" value={form.state_id} onChange={onChange("state_id")} disabled={!form.country_id} data-testid="college-state-select">
                      <option value="">{form.country_id ? "Select…" : "Select country first"}</option>
                      {statesForCountry.map((s) => (<option key={s.id} value={s.id}>{s.state_code} — {s.name}</option>))}
                    </select>
                  </div>
                  <div className="field">
                    <label className="field-label">City</label>
                    <select className="field-input" value={form.city_id} onChange={onChange("city_id")} disabled={!form.state_id} data-testid="college-city-select">
                      <option value="">{form.state_id ? "Select…" : "Select state first"}</option>
                      {citiesForState.map((ci) => (<option key={ci.id} value={ci.id}>{ci.name}</option>))}
                    </select>
                  </div>
                </div>
                <div className="field">
                  <label className="field-label">University</label>
                  <select className="field-input" value={form.university_id} onChange={onChange("university_id")} data-testid="college-university-select">
                    <option value="">Select…</option>
                    {universities.map((u) => (<option key={u.id} value={u.id}>{u.name}</option>))}
                  </select>
                </div>
              </div>

              <div className="form-section">
                <p className="form-section-title">College Admin</p>
                <div className="field">
                  <label className="field-label">Name</label>
                  <input className="field-input" placeholder="Admin full name" value={form.admin_name} onChange={onChange("admin_name")} maxLength={120} data-testid="admin-name-input" />
                </div>
                <div className="field-row">
                  <div className="field">
                    <label className="field-label">Email</label>
                    <input className="field-input" type="email" placeholder="admin@college.edu" value={form.admin_email} onChange={onChange("admin_email")} data-testid="admin-email-input" />
                  </div>
                  <div className="field">
                    <label className="field-label">Phone</label>
                    <PhoneField codeKey="admin_phone_code" numberKey="admin_phone" form={form} onChange={onChange} testPrefix="admin-phone" />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <p className="form-section-title">IT Admin</p>
                <div className="field">
                  <label className="field-label">Name</label>
                  <input className="field-input" placeholder="IT Admin full name" value={form.it_admin_name} onChange={onChange("it_admin_name")} maxLength={120} data-testid="it-admin-name-input" />
                </div>
                <div className="field-row">
                  <div className="field">
                    <label className="field-label">Email</label>
                    <input className="field-input" type="email" placeholder="it@college.edu" value={form.it_admin_email} onChange={onChange("it_admin_email")} data-testid="it-admin-email-input" />
                  </div>
                  <div className="field">
                    <label className="field-label">Phone</label>
                    <PhoneField codeKey="it_admin_phone_code" numberKey="it_admin_phone" form={form} onChange={onChange} testPrefix="it-admin-phone" />
                  </div>
                </div>
              </div>

            </div>
            <div className="modal-foot">
              <button className="btn-ghost" onClick={closeModal}>Cancel</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving} data-testid="save-college-btn">
                {saving ? <span className="btn-spinner" /> : modal.mode === "add" ? "Add College" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmId && (
        <div className="modal-overlay" onClick={() => setConfirmId(null)}>
          <div className="modal-box modal-box-sm" onClick={(e) => e.stopPropagation()} data-testid="delete-confirm-modal">
            <div className="modal-head">
              <h2 className="modal-title">Delete College</h2>
              <button className="modal-close" onClick={() => setConfirmId(null)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <p className="confirm-text">Move this college to <strong>Inactive</strong>? It will be hidden from selection lists but can be restored later.</p>
            </div>
            <div className="modal-foot">
              <button className="btn-ghost" onClick={() => setConfirmId(null)}>Cancel</button>
              <button className="btn-danger" onClick={handleDelete} disabled={deleting} data-testid="confirm-delete-btn">
                {deleting ? <span className="btn-spinner" /> : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
